import { RequiredCaption, RequiredMark } from '@/shared/ui/atoms/required-mark'
import { Input as AntInput } from 'antd'
import { forwardRef, useId } from 'react'

const { TextArea: AntTextArea } = AntInput

type AntTextAreaRef = React.ComponentRef<typeof AntTextArea>

export interface TextareaProps {
  label?: string | null
  placeholder?: string
  value?: string | null
  onChange?: React.ChangeEventHandler<any>
  height?: string
  width?: string
  status?: string
  required?: boolean
  errorData?: Record<string, string | null | undefined>
  field?: string
  rows?: number
  minRows?: number
  maxRows?: number
  disabled?: boolean
  maxLength?: number
  showCount?: boolean
  name?: string
  id?: string
  autoComplete?: string
  'aria-label'?: string
  readOnly?: boolean
  onBlur?: React.FocusEventHandler<HTMLTextAreaElement>
  onPressEnter?: React.KeyboardEventHandler<HTMLTextAreaElement>
  style?: React.CSSProperties
  className?: string
}

export const Textarea = forwardRef<AntTextAreaRef, TextareaProps>(function Textarea(
  {
    label,
    placeholder,
    value,
    onChange,
    height,
    width = '100%',
    status,
    required,
    errorData,
    field,
    rows = 4,
    minRows,
    maxRows,
    disabled,
    maxLength,
    showCount,
    id: idProp,
    name,
    autoComplete,
    'aria-label': ariaLabel,
    ...rest
  },
  ref,
) {
  const fallbackId = useId()
  const id = idProp ?? fallbackId
  return (
    <div className='flex flex-col gap-2 relative w-(width)'>
      {label && (
        <label htmlFor={id} className='text-xs font-medium text-(--form-header-title)'>
          {label}
          {required && <RequiredMark />}
        </label>
      )}
      <AntTextArea
        ref={ref}
        id={id}
        name={name ?? id}
        autoComplete={autoComplete}
        aria-label={ariaLabel ?? label ?? undefined}
        className='glasspro-textarea rounded-[1px]'
        styles={{ root: { width, height } }}
        placeholder={placeholder}
        value={value ?? undefined}
        onChange={onChange}
        rows={rows}
        autoSize={
          minRows || maxRows ? { minRows: minRows ?? rows, maxRows: maxRows ?? rows } : undefined
        }
        status={status as 'error' | 'warning' | undefined}
        disabled={disabled}
        maxLength={maxLength}
        showCount={showCount}
        {...rest}
      />
      {status === 'error' && errorData && (
        <RequiredCaption errorData={errorData} field={field || label || ''} />
      )}
    </div>
  )
})
